import React from "react";

import { Formik, Form, Field, ErrorMessage } from "formik";
import { object } from "yup";
import cc from "classcat";

import { PrimaryButton } from "./primary-button";
import { PhoneNumberInput } from "./phone-number-input";

import { UserInfoFormikValues } from "../types";
import { useContainerSize } from "../hooks";
import { baseUserInfoValidationSchema } from "../shared";
import { useOptInStore } from "../store";
import { TermsOfService } from "./terms-of-service";

const validationSchema = object().shape({ ...baseUserInfoValidationSchema });

interface UserInfoFormikProps {
  onSubmit: (values: UserInfoFormikValues) => Promise<void>;
  name: string;
  privacyUrl: string;
  tosUrl: string;
  phone: string;
  termsOfService: string;
}

export function UserInfoFormik(props: UserInfoFormikProps) {
  const size = useContainerSize();
  const confirmationError = useOptInStore((state) => state.confirmationError);
  const setPhoneNumber = useOptInStore((state) => state.setPhoneNumber);
  const onSubmit = async (opts: UserInfoFormikValues) => {
    setPhoneNumber(opts.phone || "");
    await props.onSubmit(opts);
  };

  return (
    <Formik
      initialValues={{ tosConsent: false, phone: props.phone }}
      onSubmit={onSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema}
    >
      {({ isSubmitting }) => (
        <Form>
          <div className="space-y-4">
            <div data-field className="space-y-2">
              <div className="flex">
                <div className="flex items-center h-5">
                  <Field
                    id="tosConsent"
                    name="tosConsent"
                    type="checkbox"
                    className="focus-primary text-primary h-4 w-4 border-gray-300 border-radius-sm"
                  />
                </div>
                <div className="ml-2 text-sm">
                  <label
                    htmlFor="tosConsent"
                    className="caption select-none text-subdued"
                  >
                    <TermsOfService
                      name={props.name}
                      termsOfService={props.termsOfService}
                      privacyUrl={props.privacyUrl}
                      tosUrl={props.tosUrl}
                    />
                  </label>
                </div>
              </div>
              <ErrorMessage
                component="p"
                className="caption text-critical"
                name="tosConsent"
              />
            </div>
            <div data-field className="space-y-2">
              <div
                className={cc([
                  {
                    "flex space-x-2": size === "desktop",
                    "space-y-2": size === "mobile",
                  },
                ])}
              >
                <div className="flex-1 relative">
                  <label className="sr-only" htmlFor="phone">
                    Phone Number
                  </label>
                  <div className="relative h-full">
                    <PhoneNumberInput name="phone" />
                  </div>
                </div>
                <div>
                  <PrimaryButton
                    className={cc([
                      {
                        "h-full": size === "desktop",
                        "w-full justify-center": size === "mobile",
                      },
                    ])}
                    type="submit"
                    loading={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Next Step"}
                  </PrimaryButton>
                </div>
              </div>
              <ErrorMessage
                component="p"
                className="caption text-critical"
                name="phone"
              />
            </div>
            {confirmationError && (
              <p className="caption text-critical">{confirmationError}</p>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
}
